import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Download,
  Mail,
  FileText,
  Search,
  EyeOff,
  Loader2
} from 'lucide-react';
import { WorkflowCard } from '../shared/WorkflowCard';
import { PlaybookReviewPanel } from '../shared/PlaybookReviewPanel';
import { FinanceRuleReviewPanel } from '../shared/FinanceRuleReviewPanel';
import { clauseRiskLevel, isHighRiskOrWorse } from '../shared/clauseRisk';
import { Contract, ContractAnalysisJSON, DetailedClauseJSON } from '../../../../types/contract.types';
import { WorkflowContext, ClauseDecision, SelectedClause } from '../../../../types/workflow.types';
import { apiConfig } from '../../../../services/api/apiConfig';
import { decodeHtmlEntities } from '../../../../utils/textUtils';

interface OutletContext extends WorkflowContext {
  onNextStep: () => void;
  onPrevStep: () => void;
  setSelectedForNegotiation: (clauseIds: string[]) => void;
}

export const Step4_Summary: React.FC = () => {
  const context = useOutletContext<OutletContext>();
  const {
    contract,
    analysis,
    clauseDecisions,
    selectedForNegotiation,
    isLoading,
    onNextStep,
    onPrevStep,
    toggleNegotiationSelection,
    setSelectedForNegotiation
  } = context;

  // Filter and search state
  const [hideApproved, setHideApproved] = useState(false);
  const [hideRejected, setHideRejected] = useState(false);
  const [showHighRiskOnly, setShowHighRiskOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isExporting, setIsExporting] = useState<'pdf' | 'docx' | 'summary' | null>(null);

  // Export analysis to PDF or DOCX
  const handleExport = async (format: 'pdf' | 'docx' | 'summary') => {
    if (!contract?.id || !analysis) {
      alert('No contract or analysis data available for export');
      return;
    }

    setIsExporting(format);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please log in to export');
      }

      // Build the analysis object in the format the backend expects
      const analysisForExport = {
        id: (analysis as any).id || `analysis_${contract.id}`,
        contractId: contract._id || contract.id,
        fileName: decodeHtmlEntities(contract.contractTitle) || 'Contract Analysis',
        analysisDate: new Date().toISOString(),
        executiveSynthesis: {
          executiveSummary: analysis.executive_summary?.overview || '',
          overallFavorability: analysis.executive_summary?.overall_favorability_score || 5,
          overallRisk: (analysis as any).riskAssessment?.overallRisk || 'medium',
          topRisks: analysis.executive_summary?.critical_issues?.map((i: any) => i.description || i) || [],
          recommendedActions: analysis.executive_summary?.critical_issues?.map((i: any) => i.recommended_action).filter(Boolean) || [],
          missingProtections: []
        },
        allClauses: analysis.clauses?.map((clause: any) => ({
          clauseRef: clause.reference,
          title: clause.title || clause.full_heading,
          text: clause.party_obligations || clause.summary || '',
          analysis: clause.summary || '',
          riskLevel: clauseRiskLevel(clause),
          isFavorable: clause.negotiation_analysis?.favorability === 'balanced',
          concerns: clause.problematic_subclauses?.map((s: any) => s.issue) || [],
          recommendation: clause.negotiation_analysis?.recommended_modification || ''
        })) || [],
        allNegotiationPoints: (analysis as any).negotiationPoints?.map((np: any) => ({
          clauseRef: np.clauseRef || np.point,
          issue: np.point,
          priority: np.priority || 'medium',
          currentWording: np.currentTerm || '',
          suggestedWording: np.suggestedChange || '',
          rationale: np.rationale || ''
        })) || [],
        sectionAnalyses: (analysis as any).majorSections?.map((section: any, idx: number) => ({
          sectionNumber: `${idx + 1}`,
          sectionTitle: section.sectionTitle || `Section ${idx + 1}`,
          summary: section.summary || '',
          partyObligations: section.obligations || { buyer: [], supplier: [] },
          deadlines: section.deadlines || [],
          clauses: [],
          unusualProvisions: []
        })) || [],
        metadata: {
          analysisModel: 'claude-sonnet-5-5',
          totalSections: (analysis as any).majorSections?.length || 0,
          totalClauses: analysis.clauses?.length || 0,
          processingTime: 0
        }
      };

      const response = await fetch(`${apiConfig.baseURL}/api/ai/export-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          analysis: analysisForExport,
          format: format === 'summary' ? 'pdf' : format,
          options: {
            includeExecutiveSummary: true,
            includeAllClauses: format !== 'summary',
            includeNegotiationPoints: true,
            includeSectionAnalyses: format !== 'summary',
            includeMetadata: true,
            companyName: (contract as any).supplier || undefined
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Export failed: ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = format === 'summary' ? 'pdf' : format;
      a.download = `contract-analysis-${decodeHtmlEntities(contract.contractTitle) || contract.id}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export failed:', error);
      alert(error instanceof Error ? error.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(null);
    }
  };

  // Calculate summary statistics
  const stats = useMemo(() => {
    const decisions = Object.values(clauseDecisions);
    return {
      total: analysis?.clauses?.length || 0,
      approved: decisions.filter(d => d.status === 'approved').length,
      rejected: decisions.filter(d => d.status === 'rejected').length,
      flagged: decisions.filter(d => d.status === 'flagged').length,
      pending: (analysis?.clauses?.length || 0) - decisions.filter(d => d.status !== 'pending').length,
      forNegotiation: selectedForNegotiation.length
    };
  }, [analysis?.clauses?.length, clauseDecisions, selectedForNegotiation]);

  // Get clauses grouped by decision status
  const clausesByStatus = useMemo(() => {
    if (!analysis?.clauses) return { approved: [], rejected: [], flagged: [], pending: [] };

    const result: Record<string, DetailedClauseJSON[]> = {
      approved: [],
      rejected: [],
      flagged: [],
      pending: []
    };

    analysis.clauses.forEach(clause => {
      const decision = clauseDecisions[clause.reference];
      const status = decision?.status || 'pending';
      if (result[status]) {
        result[status].push(clause);
      } else {
        result.pending.push(clause);
      }
    });

    return result;
  }, [analysis?.clauses, clauseDecisions]);

  // Get clauses selected for negotiation with details
  const negotiationItems = useMemo(() => {
    if (!analysis?.clauses) return [];

    return analysis.clauses
      .filter(clause => selectedForNegotiation.includes(clause.reference))
      .map(clause => {
        const decision = clauseDecisions[clause.reference];
        return {
          clause,
          decision,
          riskLevel: clauseRiskLevel(clause)
        };
      });
  }, [analysis?.clauses, selectedForNegotiation, clauseDecisions]);

  // Filtered clauses for display
  const filteredClauses = useMemo(() => {
    if (!analysis?.clauses) return [];

    return analysis.clauses.filter(clause => {
      const decision = clauseDecisions[clause.reference];
      const status = decision?.status || 'pending';

      // Hide approved filter
      if (hideApproved && status === 'approved') return false;

      // Hide rejected filter
      if (hideRejected && status === 'rejected') return false;

      // High risk only filter
      if (showHighRiskOnly) {
        if (!isHighRiskOrWorse(clause)) return false;
      }

      // Search query filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesRef = clause.reference.toLowerCase().includes(query);
        const matchesTitle = clause.title.toLowerCase().includes(query);
        const matchesSummary = clause.summary?.toLowerCase().includes(query);
        if (!matchesRef && !matchesTitle && !matchesSummary) return false;
      }

      return true;
    });
  }, [analysis?.clauses, clauseDecisions, hideApproved, hideRejected, showHighRiskOnly, searchQuery]);

  const handleSelectAll = () => {
    const allClauseRefs = analysis?.clauses?.map(c => c.reference) || [];
    setSelectedForNegotiation(allClauseRefs);
  };

  const handleDeselectAll = () => {
    setSelectedForNegotiation([]);
  };

  const handleSelectHighRisk = () => {
    const highRiskRefs = analysis?.clauses
      ?.filter(c => isHighRiskOrWorse(c))
      .map(c => c.reference) || [];
    setSelectedForNegotiation(highRiskRefs);
  };

  if (isLoading || !contract || !analysis) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Review Summary
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Review your decisions and select items for negotiation
          </p>
        </div>
        <button
          onClick={onNextStep}
          disabled={selectedForNegotiation.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          Generate Negotiation Email
          <Mail className="w-4 h-4" />
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Approved</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.approved}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Rejected</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.rejected}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Flagged</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.flagged}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">For Negotiation</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.forNegotiation}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Framework review. Only rendered when a playbook applied to this
          contract type - analyses predating playbook support, and contract
          types with no registered framework, simply omit this section. */}
      {analysis?.playbookReview && (
        <WorkflowCard
          title={analysis.playbookReview.playbookName}
          subtitle={`${analysis.playbookReview.perspective} \u00b7 Framework v${analysis.playbookReview.playbookVersion}`}
        >
          <PlaybookReviewPanel review={analysis.playbookReview} compact />
        </WorkflowCard>
      )}

      {/* Finance rule book. Rendered only when at least one rule applied. */}
      {analysis?.financeRuleReview && (
        <WorkflowCard
          title="Finance Rule Book"
          subtitle={`${analysis.financeRuleReview.rulesChecked} rule${analysis.financeRuleReview.rulesChecked === 1 ? '' : 's'} checked \u00b7 ${analysis.financeRuleReview.counts.VIOLATED} violated`}
        >
          <FinanceRuleReviewPanel review={analysis.financeRuleReview} />
        </WorkflowCard>
      )}

      {/* Negotiation Items Selection */}
      <WorkflowCard
        title="Items for Negotiation"
        subtitle={`${selectedForNegotiation.length} of ${filteredClauses.length} items selected${filteredClauses.length !== (analysis?.clauses?.length || 0) ? ` (${analysis?.clauses?.length || 0} total)` : ''}`}
      >
        {/* Filter Controls */}
        <div className="mb-4 flex flex-col gap-3">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clauses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filter Toggles and Selection Actions */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Filter Toggles */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setHideApproved(!hideApproved)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  hideApproved
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-300 dark:border-green-700'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <EyeOff className="w-3.5 h-3.5" />
                Hide Approved ({stats.approved})
              </button>
              <button
                onClick={() => setHideRejected(!hideRejected)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  hideRejected
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-300 dark:border-red-700'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <EyeOff className="w-3.5 h-3.5" />
                Hide Rejected ({stats.rejected})
              </button>
              <button
                onClick={() => setShowHighRiskOnly(!showHighRiskOnly)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  showHighRiskOnly
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-300 dark:border-red-700'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                High Risk Only
              </button>
            </div>

            {/* Selection Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleSelectHighRisk}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
              >
                Select High Risk
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                onClick={handleSelectAll}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Select All
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                onClick={handleDeselectAll}
                className="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        {filteredClauses.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredClauses.map((clause, clauseIndex) => {
              const isSelected = selectedForNegotiation.includes(clause.reference);
              const decision = clauseDecisions[clause.reference];
              const risk = clauseRiskLevel(clause);

              return (
                <label
                  key={clause.id || `${clause.reference}-${clauseIndex}`}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
                      : 'bg-gray-50 dark:bg-gray-800/50 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleNegotiationSelection(clause.reference)}
                    className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {clause.reference}
                      </span>
                      {risk === 'critical' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300 rounded">
                          Critical
                        </span>
                      )}
                      {risk === 'high' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded">
                          High Risk
                        </span>
                      )}
                      {risk === 'medium' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded">
                          Medium Risk
                        </span>
                      )}
                      {decision?.status && decision.status !== 'pending' && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          decision.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          decision.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {decision.status}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {clause.title}
                    </p>
                    {clause.negotiation_analysis?.recommended_modification && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 line-clamp-2">
                        Recommendation: {clause.negotiation_analysis.recommended_modification}
                      </p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {(hideApproved || hideRejected || showHighRiskOnly || searchQuery) ? (
              <div>
                <p>No clauses match your filters</p>
                <button
                  onClick={() => {
                    setHideApproved(false);
                    setHideRejected(false);
                    setShowHighRiskOnly(false);
                    setSearchQuery('');
                  }}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              'No clauses available for negotiation'
            )}
          </div>
        )}
      </WorkflowCard>

      {/* Key Terms Summary */}
      <WorkflowCard
        title="Contract Overview"
        icon={<FileText className="w-5 h-5" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Contract Details
            </h4>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Title</dt>
                <dd className="text-gray-900 dark:text-white font-medium">{decodeHtmlEntities(contract.contractTitle)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Supplier</dt>
                <dd className="text-gray-900 dark:text-white">{contract.supplier}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Value</dt>
                <dd className="text-gray-900 dark:text-white">
                  {contract.value ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: contract.paymentCurrency || 'USD'
                  }).format(contract.value) : 'N/A'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Period</dt>
                <dd className="text-gray-900 dark:text-white">
                  {new Date(contract.startDate).toLocaleDateString()} - {new Date(contract.endDate).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Analysis Summary
            </h4>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Favorability Score</dt>
                <dd className="text-gray-900 dark:text-white font-medium">
                  {analysis.executive_summary?.overall_favorability_score}/10
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Total Clauses</dt>
                <dd className="text-gray-900 dark:text-white">{stats.total}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Critical Issues</dt>
                <dd className="text-red-600 dark:text-red-400 font-medium">
                  {analysis.executive_summary?.critical_issues?.length || 0}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Review Progress</dt>
                <dd className="text-gray-900 dark:text-white">
                  {stats.total - stats.pending}/{stats.total} reviewed
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </WorkflowCard>

      {/* Export Options */}
      <WorkflowCard title="Export Options">
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => handleExport('pdf')}
            disabled={isExporting !== null}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isExporting === 'pdf' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isExporting === 'pdf' ? 'Exporting...' : 'Export as PDF'}
          </button>
          <button
            onClick={() => handleExport('docx')}
            disabled={isExporting !== null}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isExporting === 'docx' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isExporting === 'docx' ? 'Exporting...' : 'Export as DOCX'}
          </button>
          <button
            onClick={() => handleExport('summary')}
            disabled={isExporting !== null}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isExporting === 'summary' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {isExporting === 'summary' ? 'Exporting...' : 'Export Summary Report'}
          </button>
        </div>
      </WorkflowCard>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onPrevStep}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Clause Review
        </button>
        <button
          onClick={onNextStep}
          disabled={selectedForNegotiation.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          {selectedForNegotiation.length > 0
            ? `Generate Email (${selectedForNegotiation.length} items)`
            : 'Select items to continue'}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
